import { useState, useEffect, useRef } from "react";
import { Calculator } from "lucide-react";
import { cn } from "@/lib/utils/helpers";
import { COMM_FIELD_BASE, COMM_LABEL } from "@/lib/styles/companyEditTabShell";
import { useUserRole } from "@/hooks/useUserRole";
import { showSuccess, showError, formatApiError } from "@/lib/utils/notifications";
import { isCompanyDuplicateAddressCancelledError } from "@/features/company/utils/companyDuplicateAddress";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { companyEditConfirmPresentation } from "@/features/company/constants";
import { CompanyConfigEmbed } from "./ConfigTab";
import type { CompanyConfigEmbedHandle } from "../../types";
import { buildIntegrationsCompanyPayload } from "@/features/company/utils/companyIntegrationPayload";
import { sanitizeItrRatioInput } from "@/features/company/utils/sanitizeItrRatioInput";
import { SectionCard } from "@/components/ui/SectionCard";
import companyFormStyles from "../../styles/companyAdd.module.css";
import tabStyles from "../../styles/companyInfoTab.module.css";

import type { CompanyConfigTabProps, CompanyDetailsPayload } from "../../types";

export function CompanyConfigTab({
    company,
    onUpdate,
    companyId: companyIdFromRoute,
    beginHeaderSaveWork,
    endHeaderSaveOverlay,
    clearExitSaveIntent,
    completeExitNavigate,
    markIntegrationsTabSavedClean,
}: CompanyConfigTabProps) {
    const { hasPolicy, isAdmin } = useUserRole();
    const canEditPreferences = hasPolicy(["company_preference_edit"]);
    const [showConfigSaveConfirm, setShowConfigSaveConfirm] = useState(false);
    const configRef = useRef<CompanyConfigEmbedHandle>(null);

    const [itrratio, setItrratio] = useState(
        () =>
            company?.itrratio != null && company.itrratio !== ""
                ? sanitizeItrRatioInput(String(company.itrratio))
                : "",
    );

    const itrratioSyncKey = [company?.company_id ?? company?.id ?? "", String(company?.itrratio ?? "")].join(
        "\u001f",
    );

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: sync local itrratio input to the company prop when it changes (key-guarded external sync)
        setItrratio(
            company?.itrratio != null && company.itrratio !== ""
                ? sanitizeItrRatioInput(String(company.itrratio))
                : "",
        );
    }, [itrratioSyncKey]);

    const companyId = String(
        company?.company_id ?? company?.id ?? company?.fk_company_id ?? companyIdFromRoute ?? "",
    ).trim();

    const buildCompanyPayload = () =>
        buildIntegrationsCompanyPayload(
            company as Record<string, unknown> | undefined,
            {
                activate_api: String(company?.activate_api ?? ""),
                invitation_api_key: String(company?.invitation_api_key ?? ""),
            },
            isAdmin ? { itrratio } : undefined,
        );

    const clearHeaderSaveBusyState = () => {
        endHeaderSaveOverlay?.();
        clearExitSaveIntent?.();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        beginHeaderSaveWork?.();
        try {
            await onUpdate(buildCompanyPayload());
            showSuccess("Company Updated  Successfully!");
            markIntegrationsTabSavedClean?.();
            completeExitNavigate?.();
        } catch (error: unknown) {
            if (isCompanyDuplicateAddressCancelledError(error)) {
                clearExitSaveIntent?.();
                return;
            }
            showError(formatApiError(error));
            clearHeaderSaveBusyState();
        }
    };

    const handleConfigSaveClick = () => {
        if (!configRef.current?.validateConfig()) return;
        setShowConfigSaveConfirm(true);
    };

    const handleConfigSaveConfirm = async () => {
        setShowConfigSaveConfirm(false);
        if (!configRef.current?.validateConfig()) return;
        try {
            await configRef.current.saveConfigWithoutValidation();
            markIntegrationsTabSavedClean?.();
            completeExitNavigate?.();
        } catch {
            /* toast already shown by embed */
        }
    };

    return (
        <form data-testid="company-config-tab-form" onSubmit={handleSubmit} className={tabStyles.tabContent} data-tab-id="config">
            {companyId ? (
                <CompanyConfigEmbed
                    ref={configRef}
                    companyId={companyId}
                    companyDetails={company as CompanyDetailsPayload}
                />
            ) : (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    Company id is missing — reload the page or open this company from the list so Yardi / integration
                    configuration can load.
                </p>
            )}

            <div className="flex items-center gap-4">
                <button
                    data-testid="company-config-save-button"
                    type="button"
                    onClick={handleConfigSaveClick}
                    className={cn(companyFormStyles.btnPrimary, "min-w-[120px] px-10 py-2.5")}
                >
                    Save
                </button>
            </div>

            <ConfirmDialog
                isOpen={showConfigSaveConfirm}
                title="Save Changes?"
                message="You have made changes. Are you sure you want to save them?"
                {...companyEditConfirmPresentation}
                primaryLabel="Save"
                onPrimary={() => {
                    void handleConfigSaveConfirm();
                }}
                secondaryLabel="Cancel"
                onSecondary={() => setShowConfigSaveConfirm(false)}
                overlayDataTestId="company-config-save-changes-confirm-overlay"
                primaryButtonDataTestId="company-config-save-changes-confirm-primary-button"
                secondaryButtonDataTestId="company-config-save-changes-confirm-secondary-button"
                closeButtonDataTestId="company-config-save-changes-confirm-close-button"
            />

            {isAdmin && (
                <SectionCard
                    icon={Calculator}
                    title="Underwriting Config"
                    testId="company-config-underwriting-section"
                >
                    <div className="max-w-xs">
                        <label htmlFor="company-config-itrratio-input" className={COMM_LABEL}>
                            ITR Ratio
                        </label>
                        <input
                            id="company-config-itrratio-input"
                            type="text"
                            inputMode="decimal"
                            data-testid="company-config-itrratio-input"
                            value={itrratio}
                            onChange={(e) => setItrratio(sanitizeItrRatioInput(e.target.value))}
                            disabled={!canEditPreferences}
                            placeholder="Enter ratio"
                            maxLength={6}
                            className={cn(
                                COMM_FIELD_BASE,
                                "h-10 w-full",
                                !canEditPreferences && "cursor-not-allowed bg-slate-50 opacity-70",
                            )}
                        />
                    </div>
                </SectionCard>
            )}

            <button
                type="submit"
                id="config-hidden-submit"
                data-testid="company-config-hidden-submit"
                style={{ display: "none" }}
            />
        </form>
    );
}
